import React, { createContext, useContext, useState, useCallback, useEffect, FunctionComponent } from "react";

import { useWebSocket } from "common/websocket";
import { ALL_SITES_ID } from "common/consts";

import { IAlarmRuleNotification } from "scenes/alerts/Alert";


interface ILiveData {
  alertsData?: IAlarmRuleNotification;
  alarmsData: {
    [siteId: string]: ISiteAlarmsData;
  }
  queuesData: {
    [siteId: string]: ISiteQueuesData;
  }
}

export interface ISiteAlertsData {
  rule_alert: ISiteAlert[];
}

export interface ISiteAlert {
  site_id: string;
  severity: IAlarmSeverity;
  triggered_at: string;
  alarm: {
    condition_id: string;
    message: string;
  }
}

export interface ISiteQueuesData {
  queue: ISiteDataQueueSummary;
}

export interface ISiteAlarmsData {
  alarms: ISiteAlarmsDataAlarms;
}

type IAlarmSeverity = "ERROR" | "WARNING" | "INFO";
type IAlarmLink = "alarms" | "notifications";

export interface IAlarmInfo {
  name: string;
  message: string;
  level: IAlarmSeverity;
}

interface ISiteAlarmsDataAlarms {
  active_ns: string[];
  alarm_types: TAlarmsByNamespace;
  alarms_nav_tab: IAlarmLink;
}

export interface ISiteDataQueueSummary {
  receiving: boolean;
}

export type TAlarmsByNamespace = {
  [namespace: string]: IAlarmInfo[];
};


const LiveDataContext = createContext<ILiveData>({
  queuesData: {},
  alarmsData: {},
  alertsData: undefined,
});


export const AllSitesDataProvider: FunctionComponent<{}> = ({ children }) => {
  const { subscribeContext, unsubscribeContext } = useWebSocket();
  const [ alertsData, setAlertsData ] = useState<IAlarmRuleNotification | undefined>(undefined);
  const [ alarmsData, setAlarmsData ] = useState<Record<string, ISiteAlarmsData>>({});
  const [ queuesData, setQueuesData ] = useState<Record<string, ISiteQueuesData>>({});

  const handleAlarms = useCallback((body: any): void => {
    const extendData = (prev: Record<string, ISiteAlarmsData | ISiteQueuesData>) => ({
          ...prev,
          [body.site_id]: {
            ...(prev[body.site_id] ?? {}),
            [body.bucket]: body.payload,
          },
    })
    switch (body.bucket) {

      case "rule_notifications":
        return setAlertsData(body.payload);

      case "queue":
        return setQueuesData(extendData);

      case "alarms":
        return setAlarmsData(extendData);

    }
  }, []);

  useEffect(() => {
    subscribeContext(ALL_SITES_ID, "alarms", handleAlarms);
    return () => unsubscribeContext(ALL_SITES_ID, "alarms");
  }, [subscribeContext, unsubscribeContext, handleAlarms]);

  return (
    <LiveDataContext.Provider value={{
      alertsData,
      alarmsData,
      queuesData,
    }}>
      {children}
    </LiveDataContext.Provider>
  );
};

export const useAllSitesData = (): ILiveData => useContext(LiveDataContext);
